import React from "react";
import { Card, CardBody, Col, Container, Row, Table, Badge } from "reactstrap";
import { Link } from "react-router-dom";
import Navbar from "../../Landing/ForecastNestHome/navbar";
import Footer from "../../Landing/ForecastNestHome/footer";
import ResearchDisclaimer from "../../../Components/Common/ResearchDisclaimer";

/*
 * How a member forecast is recorded and scored.
 *
 * Public, and linked from under the comparison table on every forecast. A score
 * nobody can check is a number people are asked to trust; this page is what
 * makes it checkable, so it states the actual weights rather than describing
 * them in the abstract.
 *
 * THE NUMBERS HERE MIRROR backend/services/memberScoreService.js. They are
 * written out rather than imported because the calculation runs on the server
 * and shipping it to the browser only to print it would put a second copy of
 * the formula in the bundle. If the weights change there, change them here —
 * this page is the only place they are stated in words.
 */

const Step = ({ n, title, children }) => (
  <div className="d-flex mb-4">
    <div className="flex-shrink-0 me-3">
      <span
        className="avatar-title bg-primary-subtle text-primary rounded-circle fw-bold"
        style={{ width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        {n}
      </span>
    </div>
    <div className="flex-grow-1">
      <h5 className="mb-1">{title}</h5>
      <div className="text-muted">{children}</div>
    </div>
  </div>
);

const HowScoringWorks = () => {
  document.title = "How scoring works | ForecastNest";

  return (
    <React.Fragment>
      <div className="layout-wrapper landing">
        <Navbar />
        <div className="section pt-5 mt-5">
          <Container>
            <Row className="justify-content-center">
              <Col lg={9}>
                <Card>
                  <CardBody className="p-4 p-lg-5">
                    <h4 className="mb-1">How research is recorded and scored</h4>
                    <p className="text-muted mb-4">
                      What happens between asking an AI a question and seeing a number
                      next to its answer.
                    </p>

                    <h5 className="mb-3">The flow</h5>

                    <Step n="1" title="You pick a symbol and a horizon">
                      We build the prompt. It is the same structure every time and states
                      exactly what to answer — a price range, a confidence, a direction —
                      so two answers to the same question differ because the models
                      differ, not because the questions did. It carries no market data:
                      your AI does its own research, which is why a model that can search
                      the web does better here.
                    </Step>

                    <Step n="2" title="The model has to find the price itself">
                      We do not tell it what the asset is trading at. Establishing that is
                      part of the task, and it is the clearest signal available here of
                      whether a model actually looked anything up: one that cannot say what
                      something costs today has not researched it. Every answer reports the
                      price it found, and that number is kept.
                    </Step>

                    <Step n="3" title="You paste the answer back">
                      A saved forecast cannot be edited, and once settled it cannot be
                      deleted. The window it covers starts when the prompt was built, which
                      is why a prompt expires after an hour — an answer arriving later
                      would be forecasting a period that has partly happened.
                    </Step>

                    <Step n="4" title="You settle it when the window closes">
                      Two prices: what the asset cost when you asked, and what it closed at.
                      The first is the baseline everything is measured from. Asked now
                      rather than at the start, it is a fact about a moment that has passed
                      and can be read off any chart — nobody has to catch a live quote
                      before a prompt expires.
                    </Step>

                    <Step n="5" title="Everyone who answered is settled together">
                      Every model that answered that same question shares a baseline and a
                      window, so they share an outcome, and asking you for the same numbers
                      five times would only invite five chances to mistype them. The window
                      high and low are optional &mdash; the close alone is enough to produce
                      a score &mdash; but they change what that score can see. Without them
                      the only moment on record is the close.
                    </Step>

                    <div className="alert alert-light border fs-13 mb-4">
                      <strong>Why you enter the prices rather than us.</strong> Both of
                      them, at settlement. Nothing in this loop depends on us buying market
                      data, which is what keeps the research free to run and the record
                      yours. The trade-off is honest: these are self-reported numbers, so a
                      scorecard is a record you keep for yourself. Research other members
                      share is worth reading, and worth judging on its own merits.
                    </div>

                    <div className="alert alert-light border fs-13 mb-4">
                      <strong>The one place this could be gamed, and what stops it.</strong>{" "}
                      The baseline is typed in after the outcome is known, so in principle a
                      number could be chosen to flatter a score. Two things sit against
                      that. It is a public fact — anyone reading shared research can check
                      what the asset cost that day — and every model on the same question
                      independently reported what it thought the price was, printed beside
                      its forecast. A baseline that disagrees with all of them is visible to
                      any reader. Mostly, though, the scorecard is yours: a number bent to
                      look better fools only the person keeping it.
                    </div>

                    <h5 className="mb-2">Getting the price wrong</h5>
                    <p className="text-muted">
                      Whatever a forecast scores, it is discounted by how far the model's own
                      price was from the real one. A range that lands near the outcome from a
                      starting point that was wrong is luck, not analysis, and it should not
                      read like analysis.
                    </p>
                    <p className="text-muted">
                      Under a percent costs nothing. A delayed quote, an extended-hours
                      print, or the minutes between one model answering and the next being
                      asked are not research failures. Past that it falls away smoothly:
                      being 2% out halves the score, 5% takes about four fifths of it, and a
                      model that thinks a $760 index costs a dollar keeps almost none. There
                      is no cliff edge to sit just underneath.
                    </p>
                    <p className="text-muted">
                      An answer that reported no price at all is not penalised. An
                      unanswered question is not a wrong answer, and the parser that reads
                      these responses can miss a number a model did give — marking a model
                      down for our own bug would be worse than letting one through.
                    </p>

                    <h5 className="mb-2">The score</h5>
                    <p className="text-muted">
                      A settlement knows one thing for certain: where the price ended up.
                      So the score is built on the claim a forecast actually makes —
                      which way — and then adjusted by how well that claim was stated.
                    </p>

                    <div className="d-flex flex-wrap gap-3 mb-4">
                      <div className="border rounded p-3 flex-grow-1">
                        <Badge color="success" className="mb-2">Direction right</Badge>
                        <div className="fs-4 fw-bold">60 – 100</div>
                        <div className="text-muted fs-13">
                          60 for the call, up to 40 more for how it was framed.
                        </div>
                      </div>
                      <div className="border rounded p-3 flex-grow-1">
                        <Badge color="danger" className="mb-2">Direction wrong</Badge>
                        <div className="fs-4 fw-bold">0 – 60</div>
                        <div className="text-muted fs-13">
                          How far the price went the other way, relative to how far the
                          forecast said it would go. Zero is reserved &mdash; see below.
                        </div>
                      </div>
                    </div>

                    <h6 className="mb-2">What earns the other 40</h6>
                    <Table size="sm" className="align-middle fs-13">
                      <thead>
                        <tr className="text-muted fs-12">
                          <th>Component</th>
                          <th className="text-end" style={{ width: 90 }}>Weight</th>
                          <th>What it measures</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Range hit</td>
                          <td className="text-end">35%</td>
                          <td className="text-muted">
                            Whether the close landed inside the stated range. Missing by a
                            little costs less than missing by a lot.
                          </td>
                        </tr>
                        <tr>
                          <td>Commitment</td>
                          <td className="text-end">40%</td>
                          <td className="text-muted">
                            How narrow the range was, measured against the size of the move
                            it predicted. A band wide enough to cover every outcome hits
                            almost every time, so hitting is not what carries a forecast.
                          </td>
                        </tr>
                        <tr>
                          <td>Confidence</td>
                          <td className="text-end">25%</td>
                          <td className="text-muted">
                            Whether the model backed its own answer. A right call made at
                            30% is a weaker result than the same call at 90%.
                          </td>
                        </tr>
                      </tbody>
                    </Table>

                    <div className="alert alert-light border fs-13 mt-3 mb-4">
                      <strong>Confidence cuts both ways.</strong> It earns points on a
                      correct call and costs them on a wrong one. If it only ever
                      deducted, claiming certainty on everything would be the winning
                      strategy — so the worst outcome a forecast can have is being sure
                      and badly wrong, and it is scored that way.
                    </div>

                    <h5 className="mb-2">What earns a zero</h5>
                    <p className="text-muted">
                      One thing: a forecast the price never came near. If the range you
                      gave and the range the price actually traded in never overlap at
                      any point in the window, the forecast said nothing true about
                      where price would be, and it scores zero.
                    </p>
                    <p className="text-muted">
                      Anything the price did touch scores above zero, even when the
                      direction was wrong and it closed somewhere else. A call of
                      $115&ndash;$130 on a day price ran from $110.64 to $123.59 was
                      wrong about the direction, but it was not wrong about the level
                      &mdash; price spent time inside that band, and the score says so.
                      Closing inside the range is worth more again.
                    </p>
                    <p className="text-muted mb-4">
                      This is why the window high and low are worth entering when you
                      settle. Without them the only moment anyone knows about is the
                      close, so &ldquo;did price ever reach your range&rdquo; collapses
                      to &ldquo;did it finish there&rdquo; &mdash; and a forecast the
                      market passed straight through can end up scored as though it had
                      never been close.
                    </p>

                    <h5 className="mb-2">When a range counts as a direction</h5>
                    <p className="text-muted">
                      Add the two ends of the range together, as moves from the price
                      the forecast started at. A range of &minus;6.5% to +6.4% sums to
                      &minus;0.1%; one of &minus;4.1% to +4.8% sums to +0.7%. That sum
                      is the band's <em>lean</em>, and it is zero for a band sitting
                      squarely on the starting price however wide that band is.
                    </p>
                    <p className="text-muted">
                      A lean of more than <strong>0.2%</strong> counts as a call — up if
                      it is positive, down if negative. Below that, no direction was
                      called. A tenth or two of a percent inside a band several percent
                      wide is where the bounds happened to be rounded, not a view about
                      which way price goes, and reading it as one would credit a model
                      for an opinion it never expressed. Those forecasts are still
                      scored, on whether the range held and how wide it had to be, but
                      in a band that stops below 60 — declining to take a side cannot
                      score as well as taking one and being right.
                    </p>
                    <p className="text-muted">
                      Note what this does <em>not</em> depend on: the width of the range.
                      A model leaning 0.7% up inside a tight band and one leaning 0.7% up
                      inside a wide one have made the same call, and are judged the same
                      way. The width is paid for separately, in how much of the score a
                      correct call actually earns.
                    </p>

                    <h5 className="mb-2">What the score is not</h5>
                    <p className="text-muted mb-4">
                      It is not a return, a win rate, or anything you could trade on. It
                      measures how well a forecast was stated and whether it held, over
                      one window, for one model. Comparing models across a single
                      question is what the table on each forecast is for; comparing them
                      over time is what your scorecard is for. Neither is advice.
                    </p>

                    <div className="text-center">
                      <Link to="/my-research" className="btn btn-primary">
                        Go to my research
                      </Link>
                    </div>
                  </CardBody>
                </Card>

                <div className="mt-4">
                  <ResearchDisclaimer />
                </div>
              </Col>
            </Row>
          </Container>
        </div>
        <Footer />
      </div>
    </React.Fragment>
  );
};

export default HowScoringWorks;
